/**
 * 3D Room Visualizer — staging props
 *
 * Clean, low-detail props for furnishing a room: a workbench, a bike, a washer,
 * a vanity, tools on hooks … built from boxes and cylinders in flat colours, so
 * they sit well beside the panelled walls without pretending to be photographs.
 * Each builder is keyed by the prop id the staging kit uses
 * (calculations/staging/kits.ts); a curated GLB can take a prop's place later
 * through the kit's `model` field — the loader goes here too.
 *
 * Coordinates. A FLOOR prop is built standing on the floor (y = 0), centred on
 * the origin in x and z, facing +z: its back is at z = -d/2 (toward the wall it
 * stands against), its front at +d/2. The viewer places it at the staged
 * position and yaw. A HUNG or WALL prop is built in canonical wall coordinates
 * (x along the wall at its staged x, y up, z out of the wall into the room),
 * with the accessory at (x, y); the viewer applies the wall's face transform,
 * as it does for windows and doors.
 */

import * as THREE from 'three';
import { PropDef } from '../../../calculations/staging/kits';

const IN = 1 / 12;

// A small flat palette.
const C = {
  wood: 0xc49a6c, woodDark: 0x8b6b47, steel: 0x5a6068, steelLight: 0x9aa1a9, black: 0x2a2d31,
  white: 0xf2f2ee, offWhite: 0xe6e3dc, red: 0xc84b3a, blue: 0x3f6fb5, green: 0x5f9a5a, leaf: 0x4f8a48,
  yellow: 0xe0b73a, grey: 0xb9bdc2, darkGrey: 0x6c7178, plastic: 0x7d8a99, fabric: 0x8f9bab, fabricDark: 0x6a7584,
  chrome: 0xd5d9dd, porcelain: 0xf7f7f4, rubber: 0x1f2124,
};

const mat = (color: number, extra: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.6, metalness: 0.05, ...extra });

function add(group: THREE.Group, geometry: THREE.BufferGeometry, color: number, x: number, y: number, z: number, extra: Partial<THREE.MeshStandardMaterialParameters> = {}, rot?: [number, number, number]): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, mat(color, extra));
  mesh.position.set(x, y, z);
  if (rot) mesh.rotation.set(rot[0], rot[1], rot[2]);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return mesh;
}
/** A box of size w × h × d with its CENTRE at (x, y, z). */
const box = (g: THREE.Group, w: number, h: number, d: number, x: number, y: number, z: number, color: number, extra?: Partial<THREE.MeshStandardMaterialParameters>, rot?: [number, number, number]) =>
  add(g, new THREE.BoxGeometry(w, h, d), color, x, y, z, extra, rot);
/** A vertical cylinder of radius r and height h with its centre at (x, y, z). */
const cyl = (g: THREE.Group, r: number, h: number, x: number, y: number, z: number, color: number, segments = 16, extra?: Partial<THREE.MeshStandardMaterialParameters>, rot?: [number, number, number]) =>
  add(g, new THREE.CylinderGeometry(r, r, h, segments), color, x, y, z, extra, rot);
const sphere = (g: THREE.Group, r: number, x: number, y: number, z: number, color: number) =>
  add(g, new THREE.SphereGeometry(r, 14, 10), color, x, y, z);

/**
 * A side profile (points in the z–y plane: along the length, up) extruded
 * across the width, centred on x: a car body, a boat hull. The shape's x is
 * the world's z (its front at +z), its extrusion runs along the world's x.
 */
function profile(g: THREE.Group, outline: [number, number][], width: number, color: number, extra: Partial<THREE.MeshStandardMaterialParameters> = {}): THREE.Mesh {
  const shape = new THREE.Shape(outline.map(([z, y]) => new THREE.Vector2(z, y)));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false });
  const mesh = add(g, geometry, color, 0, 0, 0, extra, [0, -Math.PI / 2, 0]);
  mesh.position.x = width / 2;
  return mesh;
}

type Builder = (g: THREE.Group, size: { w: number; d: number; h: number }) => void;

/**
 * A prop staged for a photoreal model takes that model's box (stagingMeshes),
 * which can be far taller than the thing itself — a vanity whose model
 * carries its mirror, a console whose model has the TV on it. A builder that
 * stretched its body to the box would then draw a counter at head height. So
 * each body is drawn no taller than its natural height, and whatever the box
 * has above that is for what belongs up there (the mirror, the TV).
 */
const NATURAL = { workbench: 3.2, washer: 3.3, vanity: 3.0, toilet: 2.7, console: 2.2 };

// ---------------------------------------------------------------- floor props

const workbench: Builder = (g, { w, d, h: boxH }) => {
  const h = Math.min(boxH, NATURAL.workbench);
  const top = 0.15;
  box(g, w, top, d, 0, h - top / 2, 0, C.wood);
  box(g, w - 0.2, 0.1, d - 0.3, 0, h * 0.35, 0, C.woodDark); // lower shelf
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.15, h - top, 0.15, sx * (w / 2 - 0.15), (h - top) / 2, sz * (d / 2 - 0.15), C.steel);
  // A vise at one end, a toolbox and a few things on top.
  box(g, 0.5, 0.35, 0.4, w / 2 - 0.6, h + 0.17, d / 2 - 0.35, C.steel, { metalness: 0.4, roughness: 0.4 });
  box(g, 1.4, 0.55, 0.7, -w / 2 + 1.2, h + 0.28, -0.2, C.red);
  box(g, 1.4, 0.08, 0.7, -w / 2 + 1.2, h + 0.6, -0.2, C.black);
  cyl(g, 0.12, 0.6, 0.6, h + 0.3, -0.4, C.yellow, 12);
  box(g, 0.9, 0.12, 0.5, 1.4, h + 0.06, 0.2, C.woodDark);
  // Bins on the lower shelf.
  box(g, 1.2, 0.9, 1.0, -1.2, h * 0.35 + 0.5, 0, C.plastic);
  box(g, 1.2, 0.9, 1.0, 0.6, h * 0.35 + 0.5, 0, C.blue);
};

const shelfUnit: Builder = (g, { w, d, h }) => {
  const t = 0.1;
  for (const sx of [-1, 1]) box(g, t, h, d, sx * (w / 2 - t / 2), h / 2, 0, C.steel);
  const shelves = 4;
  for (let i = 0; i <= shelves; i++) box(g, w - 2 * t, t, d, 0, 0.2 + (i * (h - 0.3)) / shelves, 0, C.steelLight);
  // Bins and boxes on the shelves.
  const colours = [C.plastic, C.blue, C.grey, C.red, C.plastic];
  for (let i = 0; i < shelves; i++) {
    const y = 0.2 + (i * (h - 0.3)) / shelves + t / 2;
    const bh = (h - 0.3) / shelves - 0.35;
    box(g, (w - 0.5) * 0.55, bh, d - 0.3, -(w - 0.5) * 0.2, y + bh / 2, 0, colours[i % colours.length]);
    if (i % 2 === 0) box(g, (w - 0.5) * 0.3, bh * 0.7, d - 0.5, (w - 0.5) * 0.3, y + bh * 0.35, 0, C.wood);
  }
};

/** A bike, its handlebars turned `barTurn` radians about the steering axis (0 = square across, as on the floor). */
const bikeWith = (barTurn: number): Builder => (g, { w, h }) => {
  const r = h * 0.33;
  const wheelZ = 0;
  // Each wheel: a tyre, a rim, a hub, and spokes from the hub to the rim — a
  // thin cylinder through the hub for each pair of opposite spokes.
  const chrome = { metalness: 0.6, roughness: 0.3 };
  for (const sx of [-1, 1]) {
    const cx = sx * (w / 2 - r - 0.1);
    add(g, new THREE.TorusGeometry(r, 0.08, 10, 28), C.rubber, cx, r, wheelZ, {}, [0, 0, 0]);
    add(g, new THREE.TorusGeometry(r - 0.09, 0.025, 8, 28), C.chrome, cx, r, wheelZ, chrome);
    cyl(g, 0.07, 0.14, cx, r, wheelZ, C.chrome, 12, chrome, [Math.PI / 2, 0, 0]);
    const pairs = 14;
    for (let i = 0; i < pairs; i++) cyl(g, 0.008, 2 * (r - 0.1), cx, r, wheelZ, C.chrome, 4, chrome, [0, 0, (i / pairs) * Math.PI]);
  }
  const fx = -(w / 2 - r - 0.1), bx = (w / 2 - r - 0.1); // front, back wheel x
  // A tube from (x0, y0) to (x1, y1) at depth z: a vertical cylinder turned about z to lie along it.
  const bar = (x0: number, y0: number, x1: number, y1: number, z = wheelZ, radius = 0.06) => {
    const len = Math.hypot(x1 - x0, y1 - y0);
    cyl(g, radius, len, (x0 + x1) / 2, (y0 + y1) / 2, z, C.blue, 10, { roughness: 0.4 }, [0, 0, -Math.atan2(x1 - x0, y1 - y0)]);
  };
  // Tubes that meet a wheel go either side of it — a pair of blades or stays
  // clear of the tyre — never through it.
  const straddle = 0.14;
  const pair = (x0: number, y0: number, x1: number, y1: number) => { for (const sz of [-1, 1]) bar(x0, y0, x1, y1, wheelZ + sz * straddle, 0.04); };
  const seatX = bx * 0.45, seatY = h * 0.86, pedX = 0.05, pedY = r * 0.95, headX = fx * 0.55, headY = h * 0.8;
  pair(bx, r, seatX, seatY);     // seat stays
  pair(bx, r, pedX, pedY);       // chain stays
  bar(seatX, seatY, pedX, pedY); // seat tube
  bar(seatX, seatY, headX, headY); // top tube
  bar(headX, headY, pedX, pedY); // down tube
  pair(headX, headY, fx, r);     // fork blades
  // Crowns: where the pairs come together at the head tube, the seat and the cranks.
  box(g, 0.14, 0.12, 2 * straddle + 0.08, headX, headY, wheelZ, C.blue);
  box(g, 0.12, 0.12, 2 * straddle + 0.08, seatX, seatY, wheelZ, C.blue);
  box(g, 0.14, 0.14, 2 * straddle + 0.08, pedX, pedY, wheelZ, C.blue);
  // Axles through the hubs, out to the stays.
  for (const ax of [fx, bx]) cyl(g, 0.025, 2 * straddle + 0.1, ax, r, wheelZ, C.chrome, 8, { metalness: 0.6, roughness: 0.3 }, [Math.PI / 2, 0, 0]);
  box(g, 0.75, 0.12, 0.3, seatX, seatY + 0.05, wheelZ, C.black);
  cyl(g, 0.035, 0.45, seatX, seatY - 0.2, wheelZ, C.chrome, 8, { metalness: 0.6, roughness: 0.3 }); // seat post
  // Handlebars: a stem up from the head tube, a bar across the bike, grips at
  // its ends — turned about the steering axis by `barTurn` (0 = square across).
  const stemTop = headY + 0.45;
  const t = barTurn;
  cyl(g, 0.04, 0.45, headX, headY + 0.22, wheelZ, C.chrome, 8, { metalness: 0.6, roughness: 0.3 });
  cyl(g, 0.045, 0.3, headX - 0.12, stemTop, wheelZ, C.chrome, 8, { metalness: 0.6, roughness: 0.3 }, [0, 0, Math.PI / 2]);
  // A cylinder along x (turned about z), then swung about y: it lies along (sin t, 0, −cos t).
  cyl(g, 0.035, 1.7, headX - 0.25, stemTop, wheelZ, C.chrome, 10, { metalness: 0.6, roughness: 0.3 }, [0, Math.PI / 2 - t, Math.PI / 2]);
  for (const sz of [-1, 1]) cyl(g, 0.055, 0.4, headX - 0.25 + sz * 0.65 * Math.sin(t), stemTop, wheelZ - sz * 0.65 * Math.cos(t), C.black, 10, { roughness: 0.9 }, [0, Math.PI / 2 - t, Math.PI / 2]);
  cyl(g, 0.3, 0.06, pedX, pedY, wheelZ + straddle + 0.08, C.black, 20, {}, [Math.PI / 2, 0, 0]);
  for (const sz of [-1, 1]) box(g, 0.35, 0.08, 0.15, pedX + sz * 0.2, pedY + sz * 0.3, wheelZ + sz * (straddle + 0.16), C.black); // pedals
};
const bike = bikeWith(0);

const stepLadder: Builder = (g, { w, d, h }) => {
  // Folded flat against the wall: the two rail pairs together, standing all but
  // upright (a touch of lean so it rests on the wall), steps between the front rails.
  const lean = Math.atan2(d * 0.35, h);
  const rail = (x: number, z: number, color: number) => {
    const m = box(g, 0.12, h / Math.cos(lean), 0.14, x, h / 2, z + (h / 2) * Math.tan(lean), color);
    m.rotation.x = -lean;
  };
  for (const sx of [-1, 1]) {
    rail(sx * (w / 2 - 0.1), -d / 2 + 0.07, C.steelLight);  // back rails, against the wall
    rail(sx * (w / 2 - 0.1), -d / 2 + 0.3, C.yellow);        // front rails, folded onto them
  }
  for (let i = 1; i <= 4; i++) {
    const y = (i * h) / 5;
    box(g, w - 0.3, 0.08, 0.28, 0, y, -d / 2 + 0.3 + y * Math.tan(lean), C.yellow);
  }
  box(g, w, 0.12, 0.45, 0, h, -d / 2 + 0.2 + h * Math.tan(lean), C.yellow);
};

const binStack: Builder = (g, { w, d, h }) => {
  const n = 2;
  for (let i = 0; i < n; i++) {
    const bh = h / n - 0.05;
    box(g, w - 0.1, bh - 0.12, d - 0.1, 0, i * (h / n) + (bh - 0.12) / 2, 0, i % 2 ? C.grey : C.plastic);
    box(g, w, 0.12, d, 0, i * (h / n) + bh - 0.06, 0, C.black);
  }
};

const washer: Builder = (g, { w, d, h: boxH }) => {
  const h = Math.min(boxH, NATURAL.washer);
  box(g, w, h - 0.3, d, 0, (h - 0.3) / 2, 0, C.white, { roughness: 0.35 });
  box(g, w, 0.3, d, 0, h - 0.15, 0, C.offWhite, { roughness: 0.35 });
  cyl(g, w * 0.33, 0.08, 0, h * 0.45, d / 2 + 0.03, C.chrome, 28, { metalness: 0.5, roughness: 0.3 }, [Math.PI / 2, 0, 0]);
  cyl(g, w * 0.27, 0.06, 0, h * 0.45, d / 2 + 0.07, C.black, 28, { roughness: 0.2 }, [Math.PI / 2, 0, 0]);
  box(g, w * 0.6, 0.14, 0.06, 0, h - 0.15, d / 2 + 0.03, C.black);
  cyl(g, 0.1, 0.06, w * 0.35, h - 0.15, d / 2 + 0.05, C.chrome, 16, { metalness: 0.5 }, [Math.PI / 2, 0, 0]);
};
const dryer: Builder = (g, size) => {
  washer(g, size);
  // The dryer's door is a touch bigger and its lint panel sits above it.
  const { w, d } = size;
  const h = Math.min(size.h, NATURAL.washer);
  box(g, w * 0.5, 0.12, 0.05, 0, h * 0.8, d / 2 + 0.03, C.grey);
};

const hamper: Builder = (g, { w, d, h }) => {
  box(g, w, h - 0.1, d, 0, (h - 0.1) / 2, 0, C.fabric, { roughness: 0.9 });
  box(g, w + 0.05, 0.1, d + 0.05, 0, h - 0.05, 0, C.fabricDark);
};

const vanity: Builder = (g, { w, d, h: boxH }) => {
  // The counter at counter height whatever the box; the mirror in the box's
  // room above it when there is enough, else on the wall above as ever.
  const h = Math.min(boxH, NATURAL.vanity);
  box(g, w, h - 0.15, d - 0.1, 0, (h - 0.15) / 2, -0.05, C.woodDark);
  box(g, w + 0.1, 0.15, d, 0, h - 0.075, 0, C.porcelain, { roughness: 0.25 });
  // Doors and handles.
  box(g, w / 2 - 0.1, h - 0.6, 0.03, -w / 4, (h - 0.15) / 2, d / 2 - 0.04, C.wood);
  box(g, w / 2 - 0.1, h - 0.6, 0.03, w / 4, (h - 0.15) / 2, d / 2 - 0.04, C.wood);
  for (const sx of [-1, 1]) cyl(g, 0.03, 0.4, sx * 0.25, h * 0.5, d / 2 - 0.01, C.chrome, 8, { metalness: 0.6 }, [0, 0, 0]);
  // Basin and tap.
  cyl(g, Math.min(0.6, w / 4), 0.1, 0, h - 0.04, 0.05, C.offWhite, 28);
  cyl(g, 0.05, 0.5, 0, h + 0.25, -d / 2 + 0.35, C.chrome, 10, { metalness: 0.7, roughness: 0.25 });
  cyl(g, 0.04, 0.45, 0, h + 0.48, -d / 2 + 0.55, C.chrome, 10, { metalness: 0.7, roughness: 0.25 }, [Math.PI / 2, 0, 0]);
  // Mirror on the wall above.
  const mirrorRoom = boxH - h - 0.6;
  const mirrorH = mirrorRoom >= 1.2 ? Math.min(2.6, mirrorRoom) : 2.2;
  box(g, w * 0.8, mirrorH, 0.06, 0, h + 0.6 + mirrorH / 2 + (mirrorRoom >= 1.2 ? 0 : 0.2), -d / 2 + 0.03, C.chrome, { metalness: 0.9, roughness: 0.05 });
};

const toilet: Builder = (g, { w, d, h: boxH }) => {
  const h = Math.min(boxH, NATURAL.toilet);
  box(g, w * 0.9, h - 1.1, 0.55, 0, 1.1 + (h - 1.1) / 2, -d / 2 + 0.3, C.porcelain, { roughness: 0.25 });
  cyl(g, w / 2, 1.0, 0, 0.75, 0.25, C.porcelain, 24, { roughness: 0.25 });
  box(g, w * 0.6, 0.75, 0.6, 0, 0.375, -d / 2 + 0.5, C.porcelain, { roughness: 0.25 });
  cyl(g, w / 2 + 0.03, 0.08, 0, 1.3, 0.25, C.white, 24, { roughness: 0.3 });
  box(g, 0.25, 0.08, 0.12, -w * 0.3, h - 0.05, -d / 2 + 0.3, C.chrome, { metalness: 0.6 });
};

const plant: Builder = (g, { w, h }) => {
  const r = w / 2;
  add(g, new THREE.CylinderGeometry(r, r * 0.75, h * 0.35, 20), C.offWhite, 0, h * 0.175, 0, { roughness: 0.7 });
  cyl(g, r * 0.9, 0.05, 0, h * 0.35, 0, C.woodDark, 20);
  cyl(g, 0.05, h * 0.5, 0, h * 0.6, 0, C.woodDark, 8);
  for (const [dx, dy, dz, s] of [[0, 0.95, 0, 0.42], [0.3, 0.8, 0.1, 0.3], [-0.28, 0.78, -0.12, 0.3], [0.05, 0.7, 0.3, 0.26], [-0.1, 0.72, -0.3, 0.26]]) {
    sphere(g, r * s * 2, dx * r * 2, h * dy, dz * r * 2, C.leaf);
  }
};

const sofa: Builder = (g, { w, d, h }) => {
  const seat = h * 0.42;
  box(g, w, seat, d, 0, seat / 2, 0, C.fabricDark, { roughness: 0.95 });
  box(g, w - 1.2, h - seat, 0.6, 0, seat + (h - seat) / 2, -d / 2 + 0.3, C.fabricDark, { roughness: 0.95 });
  for (const sx of [-1, 1]) box(g, 0.6, h * 0.75, d, sx * (w / 2 - 0.3), h * 0.375, 0, C.fabricDark, { roughness: 0.95 });
  const n = Math.max(2, Math.round((w - 1.2) / 2));
  const cw = (w - 1.2) / n;
  for (let i = 0; i < n; i++) {
    box(g, cw - 0.08, 0.35, d - 0.75, -(w - 1.2) / 2 + cw * (i + 0.5), seat + 0.17, 0.2, C.fabric, { roughness: 0.95 });
    box(g, cw - 0.12, h - seat - 0.4, 0.4, -(w - 1.2) / 2 + cw * (i + 0.5), seat + (h - seat) / 2, -d / 2 + 0.7, C.fabric, { roughness: 0.95 });
  }
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.15, 0.25, 0.15, sx * (w / 2 - 0.3), 0.125, sz * (d / 2 - 0.3), C.woodDark);
};

const consoleTv: Builder = (g, { w, d, h: boxH }) => {
  const h = Math.min(boxH, NATURAL.console);
  box(g, w, h - 0.3, d, 0, 0.3 + (h - 0.3) / 2, 0, C.woodDark);
  for (const sx of [-1, 1]) box(g, 0.1, 0.3, 0.1, sx * (w / 2 - 0.2), 0.15, 0, C.black);
  box(g, w * 0.3, h - 0.7, 0.03, -w * 0.3, 0.3 + (h - 0.3) / 2, d / 2 - 0.03, C.wood);
  box(g, w * 0.3, h - 0.7, 0.03, w * 0.3, 0.3 + (h - 0.3) / 2, d / 2 - 0.03, C.wood);
  const tvW = Math.min(w * 0.85, 4.2), tvH = tvW * 0.5625;
  box(g, tvW, tvH, 0.08, 0, h + 0.3 + tvH / 2, -d / 2 + 0.4, C.black, { roughness: 0.2 });
  box(g, 0.8, 0.3, 0.6, 0, h + 0.15, -d / 2 + 0.4, C.black);
};

const bookcase: Builder = (g, { w, d, h }) => {
  const t = 0.1;
  for (const sx of [-1, 1]) box(g, t, h, d, sx * (w / 2 - t / 2), h / 2, 0, C.woodDark);
  box(g, w, t, d, 0, h - t / 2, 0, C.woodDark);
  box(g, w, h, 0.06, 0, h / 2, -d / 2 + 0.03, C.woodDark); // back panel
  const shelves = 5;
  for (let i = 0; i <= shelves - 1; i++) box(g, w - 2 * t, t, d, 0, 0.15 + (i * (h - 0.4)) / shelves, 0, C.wood);
  const colours = [C.red, C.blue, C.green, C.yellow, C.grey, C.white];
  for (let i = 0; i < shelves; i++) {
    const y = 0.15 + (i * (h - 0.4)) / shelves + t / 2;
    let x = -w / 2 + t + 0.05;
    let k = i;
    while (x < w / 2 - t - 0.3) {
      const bw = 0.12 + ((k * 7) % 5) * 0.03;
      const bh = 0.7 + ((k * 3) % 4) * 0.08;
      box(g, bw, bh, d - 0.25, x + bw / 2, y + bh / 2, 0.05, colours[k % colours.length], { roughness: 0.8 });
      x += bw + 0.02;
      k++;
    }
  }
};

// ---------------------------------------------------------------- what hung items sit in or on
// (The accessories themselves — hooks, baskets, shelves — are Trusscore's own
// models: room3d/accessoryMeshes.ts. Nothing here draws one.)

/** A storage bin on the slats. */
function bin(g: THREE.Group, x: number, y: number, w: number, h: number, d: number, color: number): void {
  add(g, new THREE.BoxGeometry(w, h, d), color, x, y + h / 2, d / 2, { roughness: 0.5 });
  box(g, w - 0.04, 0.03, d - 0.04, x, y + h - 0.03, d / 2, C.black, { roughness: 0.9 });
}

// ---------------------------------------------------------------- hung items (decoration)

type HungBuilder = (g: THREE.Group, x: number, y: number, def: PropDef) => void;

const rake: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 4;
  cyl(g, 0.04, drop + 0.4, x, y - drop / 2 + 0.2, 0.3, C.wood, 10);
  const headW = def.size.w;
  box(g, headW, 0.08, 0.08, x, y + 0.35, 0.3, C.steel, { metalness: 0.4 });
  for (let i = 0; i < 9; i++) cyl(g, 0.015, 0.45, x - headW / 2 + (headW * (i + 0.5)) / 9, y + 0.55, 0.3, C.steel, 6, { metalness: 0.4 });
};
const shovel: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 4;
  cyl(g, 0.04, drop, x, y - drop / 2 + 0.1, 0.3, C.wood, 10);
  box(g, def.size.w, 0.9, 0.06, x, y + 0.5, 0.3, C.steel, { metalness: 0.5, roughness: 0.4 });
  box(g, 0.35, 0.3, 0.06, x, y - drop + 0.3, 0.3, C.black);
};
const broom: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 4;
  cyl(g, 0.035, drop - 0.3, x, y - (drop - 0.3) / 2 + 0.2, 0.3, C.wood, 10);
  box(g, def.size.w, 0.35, 0.12, x, y - drop + 0.9, 0.3, C.steel);
  box(g, def.size.w, 0.75, 0.2, x, y - drop + 0.37, 0.3, C.yellow, { roughness: 0.9 });
};
const hose: HungBuilder = (g, x, y, def) => {
  const r = def.size.w / 2;
  for (let i = 0; i < 4; i++) add(g, new THREE.TorusGeometry(r - i * 0.03, 0.05, 8, 32), C.green, x, y - r + 0.1, 0.5 + i * 0.1, { roughness: 0.7 });
};
const wallBin: HungBuilder = (g, x, y, def) => bin(g, x, y, def.size.w, def.size.h, def.size.d, C.blue);
// Things on a shelf: (x, y) is the middle of the shelf's top surface.
const shelfTools: HungBuilder = (g, x, y) => {
  const top = y;
  cyl(g, 0.18, 0.6, x - 0.6, top + 0.3, 0.35, C.yellow, 12);
  box(g, 0.5, 0.35, 0.4, x + 0.1, top + 0.175, 0.35, C.red);
  cyl(g, 0.12, 0.5, x + 0.65, top + 0.25, 0.3, C.grey, 12);
  box(g, 0.25, 0.3, 0.25, x - 0.1, top + 0.15, 0.35, C.blue);
};
const shelfBottles: HungBuilder = (g, x, y) => {
  const top = y;
  const colours = [C.blue, C.white, C.green, C.offWhite];
  for (let i = 0; i < 4; i++) {
    const bw = 0.3 + (i % 2) * 0.1, bh = 0.6 + (i % 3) * 0.1;
    box(g, bw, bh, 0.3, x - 0.7 + i * 0.45, top + bh / 2, 0.35, colours[i], { roughness: 0.4 });
    cyl(g, 0.06, 0.1, x - 0.7 + i * 0.45, top + bh + 0.05, 0.35, C.black, 10);
  }
};
const shelfBooks: HungBuilder = (g, x, y) => {
  const top = y;
  const colours = [C.red, C.blue, C.green, C.yellow, C.grey];
  let bx = x - 0.85;
  for (let i = 0; i < 7; i++) {
    const bw = 0.14 + (i % 3) * 0.04, bh = 0.6 + (i % 4) * 0.08;
    box(g, bw, bh, 0.5, bx + bw / 2, top + bh / 2, 0.35, colours[i % colours.length], { roughness: 0.8 });
    bx += bw + 0.02;
  }
};
// A bundle in a basket: (x, y) is the middle of the basket's floor.
const basketProp: HungBuilder = (g, x, y, def) => {
  box(g, def.size.w, 0.3, def.size.d, x, y + 0.15, 0, C.fabric, { roughness: 0.95 });
  box(g, def.size.w * 0.7, 0.18, def.size.d * 0.7, x + 0.05, y + 0.38, 0.02, C.offWhite, { roughness: 0.95 });
};
const ironingBoard: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 4;
  box(g, def.size.w, drop + 0.4, 0.08, x, y - drop / 2 + 0.2, 0.32, C.fabric, { roughness: 0.9 });
  for (const sx of [-1, 1]) box(g, 0.05, drop, 0.05, x + sx * (def.size.w / 2 - 0.15), y - drop / 2 + 0.1, 0.42, C.steelLight, { metalness: 0.4 });
};
const towelBar: HungBuilder = (g, x, y, def) => {
  // Held at the bar (its mount height): the towel hangs `drop` below it, as a hung prop's does.
  const w = def.size.w;
  const hang = def.drop ?? def.size.h;
  for (const sx of [-1, 1]) cyl(g, 0.04, 0.25, x + sx * (w / 2 - 0.1), y, 0.14, C.chrome, 10, { metalness: 0.8, roughness: 0.25 }, [Math.PI / 2, 0, 0]);
  cyl(g, 0.035, w, x, y, 0.27, C.chrome, 10, { metalness: 0.8, roughness: 0.25 }, [0, 0, Math.PI / 2]);
  box(g, w * 0.6, hang, 0.08, x, y - hang / 2 + 0.02, 0.3, C.offWhite, { roughness: 0.95 });
  box(g, w * 0.6, hang * 0.6, 0.06, x, y - hang * 0.3 + 0.02, 0.38, C.blue, { roughness: 0.95 });
};

// ---------------------------------------------------------------- more hung things (garage, shed, workshop)

/**
 * Build with a floor builder into a group of its own, turn and move that
 * group, then fold every part into `g` with the group's transform baked into
 * its geometry (the hung-prop pipeline bakes each mesh's own matrix only).
 */
function placedFloorBuild(g: THREE.Group, build: Builder, size: { w: number; d: number; h: number }, rotateZ: number, at: [number, number, number]): void {
  const sub = new THREE.Group();
  build(sub, size);
  sub.rotation.z = rotateZ;
  sub.position.set(at[0], at[1], at[2]);
  sub.updateMatrixWorld(true);
  for (const child of [...sub.children]) {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) continue;
    mesh.geometry.applyMatrix4(mesh.matrixWorld);
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    g.add(mesh);
  }
}

/** A bike hung by its front wheel: the floor bike turned up on end, its front rim over the hook. */
const bikeHung: HungBuilder = (g, x, y) => {
  const size = { w: 5.6, d: 1.8, h: 3.3 };
  const r = size.h * 0.33;
  const front = -(size.w / 2 - r - 0.1); // the front wheel's x in the floor build
  // Turned a quarter the wrong way round about z, x → y: the front wheel (at
  // negative x) comes out on top, at (r, −front). Put its rim over the hook,
  // the wheel flat to the wall out at the end of the hook's prong — and the
  // handlebars swung well round on the steering axis, as anyone hanging a
  // bike turns them, so neither grip is in the wall.
  placedFloorBuild(g, bikeWith(THREE.MathUtils.degToRad(65)), size, -Math.PI / 2, [x - r, y - r + 0.1 + front, 0.5]);
};

const helmet: HungBuilder = (g, x, y, def) => {
  const r = def.size.w / 2;
  add(g, new THREE.SphereGeometry(r, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), C.red, x, y - r + 0.05, 0.5, { roughness: 0.4 });
  for (let i = -2; i <= 2; i++) box(g, 0.05, r * 0.9, 0.06, x + i * 0.14, y - r * 0.5 + 0.05, 0.5 + r * 0.95, C.black);
  box(g, r * 1.6, 0.08, r * 1.6, x, y - r + 0.05, 0.5, C.black);
};
const hockeySticks: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 4.6;
  for (const [dx, colour] of [[-0.12, C.wood], [0.12, C.red]] as const) {
    cyl(g, 0.04, drop + 0.3, x + dx, y - drop / 2 + 0.15, 0.3, colour, 8, { roughness: 0.5 }, [0, 0, 0.05 * Math.sign(dx)]);
    box(g, 0.45, 0.1, 0.06, x + dx + 0.2 * Math.sign(dx), y - drop + 0.08, 0.3, C.black, {}, [0, 0, 0.35 * Math.sign(dx)]);
  }
};
const skateboard: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 2.5;
  box(g, def.size.w * 0.95, drop + 0.2, 0.06, x, y - drop / 2 + 0.1, 0.3, C.black, { roughness: 0.8 });
  for (const sy of [0.35, drop - 0.35]) {
    box(g, 0.45, 0.08, 0.25, x, y - sy, 0.45, C.chrome, { metalness: 0.5 });
    for (const sx of [-1, 1]) cyl(g, 0.09, 0.08, x + sx * 0.3, y - sy, 0.55, C.offWhite, 12, {}, [Math.PI / 2, 0, 0]);
  }
};
const axe: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 2.3;
  cyl(g, 0.045, drop + 0.2, x, y - drop / 2 + 0.1, 0.3, C.wood, 10);
  box(g, 0.55, 0.45, 0.07, x + 0.15, y + 0.05, 0.3, C.steel, { metalness: 0.5, roughness: 0.35 });
};
const backpack: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 1.5;
  box(g, def.size.w, drop, def.size.d, x, y - drop / 2 + 0.05, def.size.d / 2 + 0.1, C.blue, { roughness: 0.9 });
  box(g, def.size.w * 0.7, drop * 0.45, 0.2, x, y - drop * 0.7, def.size.d + 0.2, C.black, { roughness: 0.9 });
  cyl(g, 0.03, 0.4, x, y + 0.05, 0.3, C.black, 8, {}, [0, 0, Math.PI / 2]);
};
const bikePump: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 2.0;
  cyl(g, 0.07, drop, x, y - drop / 2 + 0.05, 0.3, C.red, 12);
  box(g, 0.55, 0.08, 0.1, x, y + 0.05, 0.3, C.black);
  box(g, 0.5, 0.06, 0.3, x, y - drop + 0.08, 0.35, C.black);
};
const cord: HungBuilder = (g, x, y, def) => {
  const r = def.size.w / 2;
  for (let i = 0; i < 5; i++) add(g, new THREE.TorusGeometry(r - i * 0.02, 0.035, 8, 32), 0xe08a2e, x, y - r + 0.1, 0.45 + i * 0.08, { roughness: 0.7 });
};
const handTools: HungBuilder = (g, x, y, def) => {
  const drop = def.drop ?? 1.0;
  for (const [dx, head] of [[-0.15, 'trowel'], [0.15, 'fork']] as const) {
    cyl(g, 0.035, drop * 0.55, x + dx, y - drop * 0.27, 0.3, C.wood, 8);
    if (head === 'trowel') box(g, 0.18, drop * 0.45, 0.03, x + dx, y - drop * 0.78, 0.3, C.steelLight, { metalness: 0.5 });
    else for (let i = -1; i <= 1; i++) cyl(g, 0.012, drop * 0.45, x + dx + i * 0.05, y - drop * 0.78, 0.3, C.steelLight, 6, { metalness: 0.5 });
  }
};
// Things in a basket: (x, y) is the middle of the basket's floor.
const bootsBasket: HungBuilder = (g, x, y, def) => {
  for (const sx of [-1, 1]) {
    cyl(g, 0.15, def.size.h, x + sx * 0.2, y + def.size.h / 2, 0.45, C.yellow, 14, { roughness: 0.5 });
    box(g, 0.3, 0.12, 0.5, x + sx * 0.2, y + 0.06, 0.55, C.yellow, { roughness: 0.5 });
  }
};
const ballsBasket: HungBuilder = (g, x, y, def) => {
  sphere(g, 0.36, x - 0.3, y + 0.36, 0.45, 0xd97b2c);
  sphere(g, 0.3, x + 0.35, y + 0.3, 0.4, C.white);
  sphere(g, 0.2, x + 0.05, y + def.size.h - 0.25, 0.5, C.green);
};
// Things on a shelf: (x, y) is the middle of the shelf's top surface.
const shelfHelmet: HungBuilder = (g, x, y) => {
  add(g, new THREE.SphereGeometry(0.42, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), C.red, x - 0.45, y + 0.02, 0.5, { roughness: 0.4 });
  box(g, 0.7, 0.08, 0.7, x - 0.45, y + 0.04, 0.5, C.black);
  cyl(g, 0.3, 0.35, x + 0.5, y + 0.18, 0.5, C.blue, 16, { roughness: 0.8 });
  box(g, 0.5, 0.05, 0.25, x + 0.5, y + 0.03, 0.75, C.blue, { roughness: 0.8 });
};
const shelfDrill: HungBuilder = (g, x, y) => {
  box(g, 0.55, 0.25, 0.25, x - 0.5, y + 0.4, 0.5, 0x2e8b8b, { roughness: 0.5 });
  box(g, 0.2, 0.35, 0.22, x - 0.4, y + 0.18, 0.5, C.black);
  cyl(g, 0.06, 0.25, x - 0.85, y + 0.4, 0.5, C.steel, 10, { metalness: 0.5 }, [0, 0, Math.PI / 2]);
  for (let i = 0; i < 3; i++) box(g, 0.22, 0.28, 0.25, x + 0.15 + i * 0.28, y + 0.14, 0.5, C.black);
  cyl(g, 0.16, 0.14, x + 0.1, y + 0.07, 0.3, C.yellow, 16, {}, [Math.PI / 2, 0, 0]);
};

// ---- The yard's props (kits.ts YARD): drawn like floor props, outside the house.
/** A tree: a trunk and a crown of three overlapping canopies in two greens, as tall and as wide as its box. */
const tree: Builder = (g, { w, d, h }) => {
  const r = Math.min(w, d) / 2;
  const trunkH = h * 0.4;
  cyl(g, Math.max(0.15, r * 0.08), trunkH + 0.5, 0, (trunkH + 0.5) / 2, 0, C.woodDark, 10);
  const crown = h - trunkH;
  sphere(g, r * 0.9, 0, trunkH + crown * 0.45, 0, C.leaf);
  sphere(g, r * 0.7, r * 0.35, trunkH + crown * 0.62, r * 0.1, C.green);
  sphere(g, r * 0.65, -r * 0.3, trunkH + crown * 0.55, -r * 0.25, C.leaf);
  sphere(g, r * 0.5, r * 0.05, trunkH + crown * 0.5 + r * 0.45, r * 0.05, C.green);
};
/** A shrub: a low cluster of rounded clumps. */
const shrub: Builder = (g, { w, d, h }) => {
  const r = Math.min(w, d) / 2;
  const rh = h / 2;
  sphere(g, Math.min(r, rh), 0, rh, 0, C.leaf);
  sphere(g, Math.min(r, rh) * 0.8, r * 0.45, rh * 0.95, r * 0.2, C.green);
  sphere(g, Math.min(r, rh) * 0.75, -r * 0.4, rh * 0.9, -r * 0.2, C.leaf);
};

// ---------------------------------------------------------------- vehicles (Prop Studio's made props: car, truck, tires)

/** A wheel: a black tyre lying along x with a lighter hub, its bottom on the floor. */
function wheel(g: THREE.Group, r: number, width: number, x: number, z: number): void {
  cyl(g, r, width, x, r, z, C.rubber, 18, { roughness: 0.9 }, [0, 0, Math.PI / 2]);
  cyl(g, r * 0.55, width + 0.02, x, r, z, C.grey, 12, { metalness: 0.4, roughness: 0.4 }, [0, 0, Math.PI / 2]);
}

/**
 * A low-poly sedan from its side profile -- rear bumper, trunk, rear window,
 * roof, windshield, hood, nose (at +z) -- extruded across the width, with
 * the glasshouse pushed through the body as a darker band (so the windows
 * show on the sides and both ends), four wheels under it, lights at both ends.
 */
const car: Builder = (g, { w, d, h }) => {
  const r = Math.min(0.15 * h, 0.085 * d, 0.16 * w);
  const bodyW = w * 0.92;
  // Along the length (t = 0 the tail, 1 the nose) and up (u = 0 the floor, 1 the roof), feet.
  const z = (t: number) => -d / 2 + t * d;
  const y = (u: number) => u * h;
  const outline: [number, number][] = [
    [z(0.0), y(0.3)], [z(0.0), y(0.44)], [z(0.03), y(0.55)], [z(0.24), y(0.58)], [z(0.33), y(0.92)], [z(0.42), y(1.0)],
    [z(0.6), y(1.0)], [z(0.66), y(0.95)], [z(0.8), y(0.6)], [z(0.97), y(0.54)], [z(1.0), y(0.44)], [z(1.0), y(0.3)],
  ];
  profile(g, outline, bodyW, C.blue, { roughness: 0.35, metalness: 0.15 });
  // The glasshouse: the cabin's outline a little wider than the body, in tinted glass.
  const glass: [number, number][] = [[z(0.27), y(0.59)], [z(0.35), y(0.9)], [z(0.43), y(0.97)], [z(0.59), y(0.97)], [z(0.64), y(0.92)], [z(0.77), y(0.61)]];
  profile(g, glass, bodyW + w * 0.03, C.steel, { roughness: 0.2, metalness: 0.3 });
  // The sills and wheels: the body's underside, and the tyres under it.
  box(g, bodyW, y(0.3), d * 0.8, 0, y(0.3) / 2 + r * 0.6, 0, C.black);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) wheel(g, r, w * 0.12, sx * (w / 2 - w * 0.1), sz * d * 0.32);
  for (const sx of [-1, 1]) {
    box(g, w * 0.16, h * 0.07, 0.06, sx * w * 0.3, y(0.47), d / 2 - 0.02, C.white, { emissive: 0xfff2cc, emissiveIntensity: 0.3 });
    box(g, w * 0.16, h * 0.06, 0.06, sx * w * 0.3, y(0.49), -d / 2 + 0.02, C.red, { emissive: 0x7a1010, emissiveIntensity: 0.3 });
  }
  for (const sz of [-1, 1]) box(g, bodyW, h * 0.08, 0.08, 0, y(0.3) + h * 0.04, sz * (d / 2 - 0.04), C.darkGrey);
};

/**
 * A low-poly pickup from its side profile -- tailgate, open bed, cab with
 * its glass, hood, nose (at +z) -- extruded across the width, the bed cut
 * down between its sides, big wheels, lights.
 */
const truck: Builder = (g, { w, d, h }) => {
  const r = Math.min(0.18 * h, 0.09 * d, 0.18 * w);
  const bodyW = w * 0.92;
  const z = (t: number) => -d / 2 + t * d;
  const y = (u: number) => u * h;
  // The sides: tail to nose, up over the bed's side, the cab, down the hood.
  const outline: [number, number][] = [
    [z(0.0), y(0.3)], [z(0.0), y(0.62)], [z(0.4), y(0.62)], [z(0.42), y(0.95)], [z(0.47), y(1.0)],
    [z(0.68), y(1.0)], [z(0.74), y(0.68)], [z(0.98), y(0.62)], [z(1.0), y(0.5)], [z(1.0), y(0.3)],
  ];
  profile(g, outline, bodyW, C.red, { roughness: 0.4, metalness: 0.1 });
  // The bed: a dark floor below the sides' top, between them (the sides and tailgate are the profile's).
  const wall = Math.min(0.1, w * 0.03);
  box(g, bodyW - 2 * wall, 0.04, d * 0.38, 0, y(0.42), z(0.2), C.black);
  box(g, bodyW - 2 * wall, y(0.2), d * 0.02, 0, y(0.52), z(0.4), C.red, { roughness: 0.4, metalness: 0.1 }); // the cab's back wall
  // The glass through the cab, a little wider than the body.
  const glass: [number, number][] = [[z(0.44), y(0.66)], [z(0.46), y(0.95)], [z(0.66), y(0.95)], [z(0.72), y(0.68)]];
  profile(g, glass, bodyW + w * 0.03, C.steel, { roughness: 0.2, metalness: 0.3 });
  box(g, bodyW, y(0.3), d * 0.8, 0, y(0.3) / 2 + r * 0.7, 0, C.black); // the underside
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) wheel(g, r, w * 0.14, sx * (w / 2 - w * 0.1), sz * d * 0.32);
  for (const sx of [-1, 1]) {
    box(g, w * 0.14, h * 0.08, 0.06, sx * w * 0.32, y(0.5), d / 2 - 0.02, C.white, { emissive: 0xfff2cc, emissiveIntensity: 0.3 });
    box(g, w * 0.08, h * 0.12, 0.06, sx * w * 0.42, y(0.45), -d / 2 + 0.02, C.red, { emissive: 0x7a1010, emissiveIntensity: 0.3 });
  }
  box(g, bodyW, h * 0.08, 0.08, 0, y(0.3) + h * 0.04, d / 2 - 0.04, C.darkGrey);
};

/** A stack of tyres, as many as the box is tall, lying flat. */
const tires: Builder = (g, { w, d, h }) => {
  const outer = Math.min(w, d) / 2;
  const tube = outer * 0.32;
  const each = tube * 2;
  const count = Math.max(1, Math.min(8, Math.floor(h / each + 1e-6)));
  for (let i = 0; i < count; i++) {
    const t = new THREE.Mesh(new THREE.TorusGeometry(outer - tube, tube, 8, 20), mat(C.rubber, { roughness: 0.9 }));
    t.rotation.x = Math.PI / 2;
    t.position.set(0, tube + i * each, 0);
    t.castShadow = true;
    t.receiveShadow = true;
    g.add(t);
  }
};

/**
 * Floating wall shelves (the basement's "Indoor Shelves"): two or three
 * boards on steel brackets, their back on the wall, in the top of the box --
 * the box reaches down to the floor because the model is placed that high
 * (Prop Studio's offset), so the shelves hang where the model's do -- with a
 * few books and boxes on them.
 */
const indoorShelves: Builder = (g, { w, d, h }) => {
  const t = 0.12;
  // The shelves fill the top of the box: up to 4 ft of it, never within 3 ft of the floor when the box is tall.
  const band = h > 4.5 ? Math.min(4, h - 3) : h;
  const n = band > 2.6 ? 3 : 2;
  const spacing = (band - t) / (n - 1);
  const bracket = { roughness: 0.4, metalness: 0.5 };
  for (let i = 0; i < n; i++) {
    const y = h - t / 2 - i * spacing;
    box(g, w, t, d, 0, y, 0, C.wood);
    // A cleat along the wall and a bracket under each end.
    box(g, w, 0.22, 0.06, 0, y - t / 2 - 0.11, -d / 2 + 0.03, C.darkGrey, bracket);
    for (const sx of [-1, 1]) box(g, 0.08, 0.1, d * 0.8, sx * (w / 2 - 0.3), y - t / 2 - 0.05, -d * 0.1, C.darkGrey, bracket);
    // What sits on it: a row of books, a box, a jar -- a different mix a shelf.
    const top = y + t / 2;
    const colours = [C.blue, C.red, C.plastic, C.grey, C.white];
    if (i % 2 === 0) {
      for (let k = 0; k < 4; k++) box(g, 0.12, 0.7 + (k % 2) * 0.12, d * 0.6, -w * 0.3 + k * 0.14, top + (0.7 + (k % 2) * 0.12) / 2, 0, colours[k % colours.length]);
      box(g, w * 0.22, 0.5, d * 0.7, w * 0.2, top + 0.25, 0, C.plastic);
    } else {
      box(g, w * 0.18, 0.6, d * 0.6, -w * 0.25, top + 0.3, 0, C.grey);
      cyl(g, 0.16, 0.5, w * 0.15, top + 0.25, 0, C.white, 12);
      cyl(g, 0.12, 0.35, w * 0.3, top + 0.175, 0, C.blue, 12);
    }
  }
};

/**
 * A coat on a hanger: shoulders, a body tapering to the hem, the hook above.
 * `top` is the rail's height; the coat hangs `len` below it. Facing +z
 * (its front to the room) -- or `turned`, its shoulders across z, as coats
 * hang packed along a rail.
 */
const hangingCoat = (g: THREE.Group, x: number, top: number, z: number, len: number, colour: number, turned = false) => {
  const shoulder = Math.min(1.5, len * 0.55);
  const cloth = { roughness: 0.9 };
  // A box `wide` across the shoulders and `thick` front to back, the way the coat faces.
  const piece = (wide: number, tall: number, thick: number, dx: number, y: number, dz: number, c: number) =>
    (turned ? box(g, thick, tall, wide, x + dz, y, z + dx, c, cloth) : box(g, wide, tall, thick, x + dx, y, z + dz, c, cloth));
  cyl(g, 0.02, 0.22, x, top - 0.11, z, C.steel, 6, { metalness: 0.6 });  // the hook
  piece(shoulder, 0.1, 0.12, 0, top - 0.25, 0, C.darkGrey);                 // the hanger
  piece(shoulder * 0.95, len * 0.42, 0.3, 0, top - 0.3 - len * 0.21, 0, colour);                 // the chest
  piece(shoulder * 0.8, len * 0.5, 0.26, 0, top - 0.3 - len * 0.42 - len * 0.25, 0, colour);    // the skirt
  for (const sx of [-1, 1]) piece(0.22, len * 0.45, 0.22, sx * (shoulder * 0.48 + 0.08), top - 0.3 - len * 0.26, 0, colour); // the sleeves
};

/**
 * A garment rack (the laundry room's "clothes rack"): two posts on feet, a
 * rail across the top, coats and shirts on hangers along it, a basket on
 * the bottom rail.
 */
const clothesRack: Builder = (g, { w, d, h }) => {
  const post = 0.08;
  const chrome = { metalness: 0.6, roughness: 0.3 };
  const px = w / 2 - 0.15;
  for (const sx of [-1, 1]) {
    cyl(g, post / 2, h - 0.15, sx * px, (h - 0.15) / 2 + 0.1, 0, C.steel, 10, chrome);          // the post
    box(g, 0.12, 0.1, d * 0.9, sx * px, 0.05, 0, C.steel, chrome);                                  // its foot
    for (const sz of [-1, 1]) cyl(g, 0.08, 0.05, sx * px, 0.04, sz * (d * 0.45 - 0.08), C.black, 10, {}, [Math.PI / 2, 0, 0]); // castors
  }
  cyl(g, post / 2, w - 0.3 + 0.1, 0, h - 0.1, 0, C.steel, 10, chrome, [0, 0, Math.PI / 2]);       // the top rail
  cyl(g, post / 2, w - 0.3 + 0.1, 0, 0.5, 0, C.steel, 10, chrome, [0, 0, Math.PI / 2]);          // the bottom rail
  box(g, w * 0.4, 0.35, d * 0.6, 0, 0.6 + 0.175, 0, C.plastic, { roughness: 0.8 });               // a basket on it
  // The clothes: as many as fit at a hand's spacing, hems well off the basket.
  const colours = [0x4a5a7a, 0xb8a98c, 0x3b3b3b, 0x8c3b3b, 0x6b7f62, 0xd9d2c4, 0x2f4f6f];
  // Packed along the rail, shoulders across the rack, a hand apart.
  const step = 0.34;
  const n = Math.max(1, Math.min(14, Math.floor((w - 0.6) / step)));
  const len = Math.max(1.6, h - 0.1 - 1.2);
  for (let i = 0; i < n; i++) hangingCoat(g, -((n - 1) * step) / 2 + i * step, h - 0.1, 0, len * (i % 3 === 1 ? 0.8 : 1), colours[i % colours.length], true);
};

/** A stainless prep table (the commercial kitchen's): a steel top on four legs, a shelf below, a board and a pot on it. */
const prepTable: Builder = (g, { w, d, h }) => {
  const steel = { metalness: 0.75, roughness: 0.3 };
  const leg = 0.12;
  box(g, w, 0.12, d, 0, h - 0.06, 0, C.steelLight, steel);                                        // the top
  box(g, w, 0.25, 0.04, 0, h - 0.25, -d / 2 + 0.02, C.steelLight, steel);                         // its back splash
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, leg, h - 0.12, leg, sx * (w / 2 - leg / 2 - 0.05), (h - 0.12) / 2, sz * (d / 2 - leg / 2 - 0.05), C.steel, steel);
  box(g, w - 0.4, 0.06, d - 0.4, 0, 0.6, 0, C.steelLight, steel);                                  // the undershelf
  box(g, w * 0.3, 0.6, d * 0.6, -w * 0.2, 0.63 + 0.3, 0, C.grey);                                   // a crate on it
  box(g, Math.min(1.6, w * 0.3), 0.06, Math.min(1.1, d * 0.5), -w * 0.22, h + 0.03, 0.05, C.wood);  // a cutting board
  cyl(g, 0.45, 0.5, w * 0.25, h + 0.25, 0, C.steelLight, 16, steel);                                // a stock pot
  cyl(g, 0.47, 0.04, w * 0.25, h + 0.52, 0, C.steel, 16, steel);                                    // its lid
};

/**
 * A commercial range (the kitchen's "Commercial Cooker"): a stainless body,
 * burner grates across the top, oven doors with bar handles, a row of knobs,
 * a back guard -- and, when the box reaches well above a range (the model
 * has its wall-mounted hood), the hood: a canopy over the cooktop against the
 * wall and a chimney up to the top of the box.
 */
const commercialCooker: Builder = (g, { w, d, h: boxH }) => {
  const steel = { metalness: 0.75, roughness: 0.3 };
  const hooded = boxH > 4.6;
  const h = hooded ? 3.2 : boxH;
  const top = h * 0.92;
  box(g, w, top - 0.4, d, 0, 0.4 + (top - 0.4) / 2, 0, C.steelLight, steel);                   // the body
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.15, 0.4, 0.15, sx * (w / 2 - 0.2), 0.2, sz * (d / 2 - 0.2), C.steel, steel); // legs
  box(g, w, 0.08, d, 0, top + 0.04, 0, C.black, { roughness: 0.6 });                             // the cooktop
  box(g, w, h - top - 0.08, 0.1, 0, top + 0.08 + (h - top - 0.08) / 2, -d / 2 + 0.05, C.steelLight, steel); // the back guard
  // Burner grates: as many as the width takes, two rows when it is deep enough.
  const cols = Math.max(2, Math.min(8, Math.round(w / 1.1)));
  const rows = d > 2.2 ? 2 : 1;
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const x = -w / 2 + (w / cols) * (c + 0.5);
      const z = rows === 1 ? -d * 0.1 : -d * 0.3 + r * d * 0.42;
      cyl(g, Math.min(0.38, w / cols * 0.4), 0.05, x, top + 0.1, z, C.black, 14, { roughness: 0.8 });   // the burner's ring
      cyl(g, 0.12, 0.07, x, top + 0.11, z, C.darkGrey, 10, steel);                                 // its cap
      box(g, Math.min(0.9, w / cols * 0.9), 0.05, Math.min(0.9, d * 0.42), x, top + 0.14, z, C.black, { roughness: 0.8 }); // the grate
    }
  }
  // The front: the knobs along a strip, oven doors with bar handles below.
  const knobs = Math.max(3, Math.min(10, cols * (rows === 2 ? 2 : 1)));
  for (let k = 0; k < knobs; k++) cyl(g, 0.07, 0.08, -w / 2 + (w / knobs) * (k + 0.5), top - 0.15, d / 2 + 0.04, C.black, 10, {}, [Math.PI / 2, 0, 0]);
  const doors = w > 4 ? 2 : 1;
  for (let i = 0; i < doors; i++) {
    const dw = (w - 0.3) / doors - 0.1;
    const x = -w / 2 + 0.15 + (w - 0.3) / doors * (i + 0.5);
    box(g, dw, top - 0.4 - 0.5, 0.04, x, 0.4 + (top - 0.9) / 2 - 0.05, d / 2 + 0.02, C.steel, steel);     // the door
    cyl(g, 0.04, dw * 0.8, x, top - 0.6, d / 2 + 0.14, C.steelLight, 10, steel, [0, 0, Math.PI / 2]);     // its handle
  }
  if (hooded) {
    // The hood: its canopy's bottom about 3 ft over the cooktop (lower in a short box), its chimney to the box's top.
    const canopyH = 1.2;
    const bottom = Math.min(boxH - canopyH, Math.max(top + 2.4, boxH - 3.7));
    const hw = w * 0.9, hd = d * 0.8;
    box(g, hw, canopyH, hd, 0, bottom + canopyH / 2, -d / 2 + hd / 2, C.steelLight, steel);                    // the canopy
    box(g, hw - 0.3, 0.06, hd - 0.3, 0, bottom + 0.03, -d / 2 + hd / 2 + 0.1, C.darkGrey, { roughness: 0.6 }); // its filters
    box(g, hw, 0.08, hd, 0, bottom + canopyH - 0.04, -d / 2 + hd / 2, C.steel, steel);                          // its lip
    const chimneyH = boxH - bottom - canopyH;
    if (chimneyH > 0.2) box(g, Math.min(hw * 0.6, 2.6), chimneyH, hd * 0.6, 0, bottom + canopyH + chimneyH / 2, -d / 2 + hd * 0.3, C.steelLight, steel);
  }
};

/** A catering trolley (the kitchen's "Trolley cart"): a steel frame on castors, two shelves, a coffee machine, carafes and cups on top. */
const trolleyCart: Builder = (g, { w, d, h }) => {
  const steel = { metalness: 0.7, roughness: 0.35 };
  const counter = Math.min(h, 3.1);
  const leg = 0.1;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    box(g, leg, counter - 0.3, leg, sx * (w / 2 - 0.15), 0.3 + (counter - 0.3) / 2, sz * (d / 2 - 0.15), C.steel, steel);  // the posts
    cyl(g, 0.15, 0.08, sx * (w / 2 - 0.15), 0.15, sz * (d / 2 - 0.15), C.black, 12, {}, [0, 0, Math.PI / 2]);           // castors
  }
  box(g, w, 0.08, d, 0, counter - 0.04, 0, C.darkGrey, { roughness: 0.6 });        // the counter
  box(g, w - 0.3, 0.05, d - 0.3, 0, 1.3, 0, C.steelLight, steel);                   // the middle shelf
  box(g, w - 0.3, 0.05, d - 0.3, 0, 0.45, 0, C.steelLight, steel);                  // the bottom shelf
  box(g, Math.min(1.6, w * 0.35), 0.5, d * 0.55, -w * 0.2, 0.48 + 0.25, 0, C.blue); // a crate below
  // On the counter: the machine at one end, carafes under it, cups and jars along the rest.
  const mw = Math.min(1.6, w * 0.3), mh = Math.min(2.0, h - counter - 0.1);
  const mx = -w / 2 + mw / 2 + 0.2;
  box(g, mw, mh, d * 0.6, mx, counter + mh / 2, -d * 0.1, C.black, { roughness: 0.5 });   // the coffee machine
  box(g, mw, 0.2, d * 0.6, mx, counter + mh - 0.1, -d * 0.1, C.darkGrey, steel);          // its top
  for (const k of [-1, 1]) cyl(g, 0.3, 0.55, mx + k * mw * 0.25, counter + 0.28, d * 0.2, 0x9fb8c8, 12, { roughness: 0.1, metalness: 0.2 }); // carafes
  const rest = w - mw - 0.4;
  const n = Math.max(2, Math.min(6, Math.floor(rest / 0.6)));
  for (let i = 0; i < n; i++) {
    const x = mx + mw / 2 + 0.3 + (rest / n) * (i + 0.5);
    if (i % 2 === 0) cyl(g, 0.17, 0.3, x, counter + 0.15, 0, C.white, 10);                                  // a cup
    else cyl(g, 0.22, 0.6, x, counter + 0.3, -d * 0.15, i % 4 === 1 ? C.red : C.darkGrey, 12, { roughness: 0.6 }); // a jar
  }
  if (h > counter + 2.2) box(g, w * 0.8, 0.06, 0.06, 0, h - 0.05, -d / 2 + 0.1, C.steel, steel); // a rail at the top
};

/** A wine cellar column (the kitchen's "Wine Fridge"): a stainless cabinet with a glass door, racks of bottles behind it, a long bar handle. */
const wineFridge: Builder = (g, { w, d, h }) => {
  const steel = { metalness: 0.75, roughness: 0.3 };
  const wall = 0.08;
  box(g, w, 0.3, d, 0, 0.15, 0, C.black);                                                           // the plinth
  box(g, wall, h - 0.3, d, -w / 2 + wall / 2, 0.3 + (h - 0.3) / 2, 0, C.steelLight, steel);         // the sides
  box(g, wall, h - 0.3, d, w / 2 - wall / 2, 0.3 + (h - 0.3) / 2, 0, C.steelLight, steel);
  box(g, w, wall, d, 0, h - wall / 2, 0, C.steelLight, steel);                                      // the top
  box(g, w - 2 * wall, h - 0.3 - wall, 0.06, 0, 0.3 + (h - 0.3 - wall) / 2, -d / 2 + 0.03, C.black); // the dark back
  // The racks, each a row of bottles lying with their necks to the door.
  const racks = Math.max(3, Math.min(8, Math.floor((h - 0.8) / 0.75)));
  const pitch = (h - 0.8) / racks;
  const across = Math.max(2, Math.min(6, Math.floor((w - 0.4) / 0.33)));
  const colours = [0x2f4f2f, 0x5a1f1f, 0x3b3b2a, 0x6b2a2a, 0x1f3b2f];
  for (let r = 0; r < racks; r++) {
    const y = 0.5 + r * pitch;
    box(g, w - 2 * wall - 0.05, 0.04, d - 0.3, 0, y, -0.05, C.steel, steel);                       // the rack
    for (let i = 0; i < across; i++) {
      const x = -(across - 1) * 0.33 / 2 + i * 0.33;
      cyl(g, 0.12, d * 0.55, x, y + 0.14, -0.12, colours[(r + i) % colours.length], 10, { roughness: 0.35 }, [Math.PI / 2, 0, 0]); // a bottle
      cyl(g, 0.05, d * 0.2, x, y + 0.14, d * 0.25 - 0.12, C.darkGrey, 8, {}, [Math.PI / 2, 0, 0]);  // its neck
    }
  }
  // The glass door across the front, its frame and a long handle.
  box(g, w - 2 * wall, h - 0.3 - wall, 0.03, 0, 0.3 + (h - 0.3 - wall) / 2, d / 2 - 0.02, 0x9fb8c8, { transparent: true, opacity: 0.3, roughness: 0.05, metalness: 0.3 });
  box(g, w, 0.12, 0.05, 0, h - 0.14, d / 2, C.steelLight, steel);                                   // the frame's top rail
  box(g, w, 0.12, 0.05, 0, 0.36, d / 2, C.steelLight, steel);                                       // and bottom rail
  cyl(g, 0.035, (h - 0.8) * 0.8, w / 2 - 0.2, 0.3 + (h - 0.3) / 2, d / 2 + 0.12, C.steelLight, 10, steel); // the handle
  for (const sy of [-1, 1]) box(g, 0.06, 0.06, 0.14, w / 2 - 0.2, 0.3 + (h - 0.3) / 2 + sy * (h - 0.8) * 0.4, d / 2 + 0.06, C.steelLight, steel); // its standoffs
};

/** A kitchen island (the kitchen's "Kitchen Island", an open-frame one): a butcher-block top on a dark frame, a slatted shelf below, a towel rail, a bowl and a board on top. */
const kitchenIsland: Builder = (g, { w, d, h }) => {
  const frame = { roughness: 0.6, metalness: 0.2 };
  const leg = 0.12;
  const topT = 0.15;
  box(g, w, topT, d, 0, h - topT / 2, 0, C.wood);                                                  // the butcher-block top
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, leg, h - topT, leg, sx * (w / 2 - leg / 2 - 0.04), (h - topT) / 2, sz * (d / 2 - leg / 2 - 0.04), C.black, frame); // the legs
  for (const sz of [-1, 1]) box(g, w - 0.3, 0.1, 0.06, 0, h - topT - 0.08, sz * (d / 2 - 0.07), C.black, frame);   // the apron, front and back
  // A slatted shelf a third of the way up, with a basket and a stack of bowls on it.
  const shelfY = h * 0.3;
  const slats = Math.max(3, Math.min(8, Math.round(d / 0.3)));
  for (let i = 0; i < slats; i++) box(g, w - 0.3, 0.05, 0.14, 0, shelfY, -d / 2 + 0.2 + (d - 0.4) * (i + 0.5) / slats, C.wood);
  for (const sz of [-1, 1]) box(g, w - 0.3, 0.08, 0.06, 0, shelfY - 0.05, sz * (d / 2 - 0.12), C.black, frame); // its rails
  box(g, Math.min(1.3, w * 0.35), 0.5, d * 0.6, -w * 0.22, shelfY + 0.28, 0, C.plastic, { roughness: 0.9 }); // a basket
  for (let i = 0; i < 3; i++) cyl(g, 0.3 - i * 0.03, 0.08, w * 0.22, shelfY + 0.07 + i * 0.08, 0, C.white, 14);  // bowls
  cyl(g, 0.03, d - 0.3, w / 2 + 0.1, h - 0.5, 0, C.steel, 8, { metalness: 0.7, roughness: 0.3 }, [Math.PI / 2, 0, 0]); // the towel rail
  box(g, 0.08, 0.5, 0.3, w / 2 + 0.1, h - 0.75, d * 0.1, C.white, { roughness: 0.9 });               // a towel on it
  box(g, Math.min(1.4, w * 0.35), 0.06, Math.min(1.0, d * 0.45), -w * 0.2, h + 0.03, 0, C.woodDark); // a cutting board
  cyl(g, Math.min(0.5, d * 0.2), 0.3, w * 0.22, h + 0.15, 0, C.steelLight, 16, { metalness: 0.7, roughness: 0.3 }); // a bowl
};

/** A kitchen counter with a sink (the kitchen's "Kitchen Counter"): stainless base cabinets with doors, a worktop with a sink basin and a tall faucet, a back splash. */
const kitchenCounter: Builder = (g, { w, d, h }) => {
  const steel = { metalness: 0.75, roughness: 0.3 };
  const topT = 0.12;
  box(g, w, 0.3, d - 0.2, 0, 0.15, -0.1, C.black);                                                   // the plinth, set back
  box(g, w, h - 0.3 - topT, d, 0, 0.3 + (h - 0.3 - topT) / 2, 0, C.steelLight, steel);                // the cabinets
  box(g, w, topT, d, 0, h - topT / 2, 0, C.steel, steel);                                             // the worktop
  box(g, w, 0.35, 0.05, 0, h + 0.17, -d / 2 + 0.03, C.steelLight, steel);                             // the back splash
  // The doors: as many as the width takes, each with a short bar handle.
  const doors = Math.max(1, Math.min(6, Math.round(w / 1.6)));
  for (let i = 0; i < doors; i++) {
    const dw = w / doors - 0.1;
    const x = -w / 2 + (w / doors) * (i + 0.5);
    box(g, dw, h - 0.3 - topT - 0.15, 0.03, x, 0.3 + (h - 0.3 - topT) / 2, d / 2 + 0.015, C.steelLight, steel);
    cyl(g, 0.025, 0.5, x + dw * 0.3, h - topT - 0.5, d / 2 + 0.07, C.steel, 8, steel);
  }
  // The sink: a basin let into the worktop, a faucet behind it, a drain rack beside.
  const sw = Math.min(2.0, w * 0.4), sd = d * 0.7;
  const sx = w * 0.15;
  box(g, sw, 0.5, sd, sx, h - 0.25 - 0.01, 0, C.darkGrey, steel);                                   // the basin's walls
  box(g, sw - 0.12, 0.02, sd - 0.12, sx, h - 0.5 + 0.02, 0, C.black, { roughness: 0.4 });            // its floor (darker, seen from above)
  box(g, sw + 0.1, 0.03, sd + 0.1, sx, h + 0.015, 0, C.steelLight, steel);                            // its rim
  cyl(g, 0.05, 0.9, sx, h + 0.45, -d / 2 + 0.25, C.steelLight, 10, steel);                            // the faucet's column
  cyl(g, 0.035, 0.7, sx, h + 0.88, -d / 2 + 0.25 + 0.35, C.steelLight, 8, steel, [Math.PI / 2, 0, 0]); // its spout
  box(g, Math.min(1.2, w * 0.2), 0.08, d * 0.6, -w * 0.25, h + 0.04, 0, C.steel, steel);              // a drain rack
  for (let i = 0; i < 3; i++) cyl(g, 0.17, 0.3, -w * 0.25 - 0.35 + i * 0.35, h + 0.23, 0, C.white, 10); // pots drying on it
};

/** A floor-standing deep fryer (the kitchen's "Deep Fryer"): a narrow stainless cabinet with a door, the vat well on top with a lid, two baskets hung on a rail at the back, a control panel at the front. */
const deepFryer: Builder = (g, { w, d, h }) => {
  const steel = { metalness: 0.75, roughness: 0.3 };
  const topY = h * 0.72;
  box(g, w, 0.25, d - 0.15, 0, 0.125, -0.05, C.black);                                                 // the plinth
  box(g, w, topY - 0.25, d, 0, 0.25 + (topY - 0.25) / 2, 0, C.steelLight, steel);                       // the cabinet
  box(g, w - 0.2, topY - 0.7, 0.03, 0, 0.3 + (topY - 0.7) / 2, d / 2 + 0.015, C.steelLight, steel);     // its door
  cyl(g, 0.025, w * 0.5, 0, topY - 0.55, d / 2 + 0.07, C.steel, 8, steel, [0, 0, Math.PI / 2]);        // the door's handle
  box(g, w, 0.1, d, 0, topY + 0.05, 0, C.steel, steel);                                                 // the top deck
  // The vat: a well let into the deck, its hinged lid propped open against the back guard.
  const vw = w - 0.3, vd = d * 0.55;
  box(g, vw, 0.35, vd, 0, topY - 0.08, d * 0.05, C.darkGrey, steel);
  box(g, vw - 0.1, 0.02, vd - 0.1, 0, topY - 0.24, d * 0.05, 0x6b5a2a, { roughness: 0.2, metalness: 0.1 }); // the oil
  box(g, w, h - topY - 0.1, 0.08, 0, topY + 0.1 + (h - topY - 0.1) / 2, -d / 2 + 0.04, C.steelLight, steel); // the back guard
  box(g, vw, 0.04, vd, 0, topY + 0.1 + vd * 0.45, -d / 2 + 0.1 + 0.2, C.steelLight, steel, [-1.2, 0, 0]);   // the lid, propped
  // Two baskets hung from a rail across the back guard, handles up.
  cyl(g, 0.03, w - 0.2, 0, h - 0.3, -d / 2 + 0.15, C.steel, 8, steel, [0, 0, Math.PI / 2]);           // the rail
  for (const sx of [-1, 1]) {
    const bx = sx * (w / 4);
    box(g, w * 0.36, 0.3, 0.3, bx, h - 0.55, -d / 2 + 0.3, C.steel, { metalness: 0.6, roughness: 0.5, wireframe: true }); // a basket (mesh)
    cyl(g, 0.02, 0.5, bx, h - 0.25, -d / 2 + 0.3, C.black, 8, {}, [0.35, 0, 0]);                       // its handle
  }
  // The controls: a small panel on the front edge of the deck with a dial and a light.
  box(g, w * 0.6, 0.18, 0.06, 0, topY + 0.02, d / 2 - 0.02, C.black, { roughness: 0.6 });
  cyl(g, 0.05, 0.05, -w * 0.15, topY + 0.02, d / 2 + 0.03, C.steelLight, 10, steel, [Math.PI / 2, 0, 0]);
  box(g, 0.05, 0.05, 0.04, w * 0.15, topY + 0.02, d / 2 + 0.02, C.red, { emissive: 0x7a1010, emissiveIntensity: 0.5 });
};

/** A stainless refrigerator (the kitchen's "Fridge", a French-door one): two doors above with long bar handles, a freezer drawer below, a plinth, a water dispenser on one door. */
const fridge: Builder = (g, { w, d, h }) => {
  const steel = { metalness: 0.75, roughness: 0.3 };
  const gap = 0.03;
  const drawerH = Math.min(h * 0.3, 2.2);
  box(g, w, 0.2, d - 0.2, 0, 0.1, -0.1, C.black);                                                     // the plinth
  box(g, w, h - 0.2, d - 0.06, 0, 0.2 + (h - 0.2) / 2, -0.03, C.steelLight, steel);                      // the cabinet
  box(g, w - 0.1, drawerH - 0.25, 0.05, 0, 0.3 + (drawerH - 0.25) / 2, d / 2 - 0.02, C.steelLight, steel); // the freezer drawer
  cyl(g, 0.035, w * 0.6, 0, 0.3 + drawerH - 0.45, d / 2 + 0.12, C.steel, 10, steel, [0, 0, Math.PI / 2]); // its handle
  const doorsY = 0.3 + drawerH + gap;
  const doorsH = h - doorsY - 0.1;
  for (const sx of [-1, 1]) {
    box(g, w / 2 - 0.07, doorsH, 0.05, sx * (w / 4 + 0.01), doorsY + doorsH / 2, d / 2 - 0.02, C.steelLight, steel); // a door
    cyl(g, 0.035, doorsH * 0.6, sx * 0.12, doorsY + doorsH / 2, d / 2 + 0.12, C.steel, 10, steel);          // its handle, by the split
    for (const sy of [-1, 1]) box(g, 0.06, 0.06, 0.14, sx * 0.12, doorsY + doorsH / 2 + sy * doorsH * 0.28, d / 2 + 0.06, C.steel, steel);
  }
  box(g, w * 0.22, doorsH * 0.3, 0.02, -w * 0.25, doorsY + doorsH * 0.62, d / 2 + 0.04, C.black, { roughness: 0.5 }); // the dispenser
  box(g, w * 0.16, doorsH * 0.05, 0.02, -w * 0.25, doorsY + doorsH * 0.74, d / 2 + 0.05, C.blue, { emissive: 0x1a3a6a, emissiveIntensity: 0.4 }); // its panel
};

/** A two-tier utility trolley (the kitchen's "Trolly Carts" models): a black tubular frame with a push handle, two tray shelves with lips, castors, a few things in the trays. */
const utilityTrolley: Builder = (g, { w, d, h }) => {
  const tube = 0.05;
  const black = { roughness: 0.5, metalness: 0.3 };
  const tiers = h > 3.2 ? 3 : 2;
  const trayH = 0.22;
  const trayYs = Array.from({ length: tiers }, (_, i) => 0.35 + (i * (h - 0.8)) / (tiers - 1));
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    cyl(g, tube / 2, h - 0.3, sx * (w / 2 - 0.1), 0.3 + (h - 0.3) / 2, sz * (d / 2 - 0.1), C.black, 8, black);  // the posts
    cyl(g, 0.1, 0.06, sx * (w / 2 - 0.1), 0.1, sz * (d / 2 - 0.1), C.darkGrey, 10, {}, [0, 0, Math.PI / 2]);     // castors
  }
  cyl(g, tube / 2, d - 0.2, -w / 2 + 0.1, h - 0.03, 0, C.black, 8, black, [Math.PI / 2, 0, 0]);  // the push handle, across one end at the top
  cyl(g, tube / 2, d - 0.2, w / 2 - 0.1, h - 0.03, 0, C.black, 8, black, [Math.PI / 2, 0, 0]);
  const fill = [C.white, C.plastic, C.blue, C.grey];
  trayYs.forEach((y, i) => {
    box(g, w - 0.1, 0.04, d - 0.1, 0, y, 0, C.black, black);                                              // the tray's floor
    for (const sx of [-1, 1]) box(g, 0.04, trayH, d - 0.1, sx * (w / 2 - 0.07), y + trayH / 2, 0, C.black, black); // its lips
    for (const sz of [-1, 1]) box(g, w - 0.1, trayH, 0.04, 0, y + trayH / 2, sz * (d / 2 - 0.07), C.black, black);
    // In it: a couple of tubs and a stack of cloths.
    box(g, w * 0.3, 0.3, d * 0.5, -w * 0.25, y + 0.17, 0, fill[(i * 2) % fill.length], { roughness: 0.8 });
    cyl(g, Math.min(0.22, w * 0.12), 0.35, w * 0.2, y + 0.2, -d * 0.15, fill[(i * 2 + 1) % fill.length], 10);
    if (i === tiers - 1) box(g, w * 0.22, 0.12, d * 0.4, w * 0.22, y + 0.08, d * 0.2, C.white, { roughness: 0.9 });
  });
};

const FLOOR_BUILDERS: Record<string, Builder> = {
  'workbench': workbench, 'shelf-unit': shelfUnit, 'bike': bike, 'step-ladder': stepLadder, 'bin-stack': binStack,
  'washer': washer, 'dryer': dryer, 'hamper': hamper, 'vanity': vanity, 'toilet': toilet, 'plant': plant,
  'sofa': sofa, 'console': consoleTv, 'bookcase': bookcase, 'indoor-shelves': indoorShelves, 'clothes-rack': clothesRack,
  'prep-table': prepTable, 'kitchen-shelving': shelfUnit, 'kitchen-bins': utilityTrolley, 'commercial-cooker': commercialCooker, 'trolley-cart': trolleyCart, 'wine-fridge': wineFridge, 'kitchen-island': kitchenIsland, 'kitchen-counter': kitchenCounter, 'deep-fryer': deepFryer, 'fridge': fridge,
  'tree': tree, 'shrub': shrub,
  'car': car, 'truck': truck, 'tires': tires,
};
/** Coats on a hanger, hung from a single hook (the laundry room's "Coats hanging"): a hanger on the hook, a coat below it, a second behind. */
const coatsHanging: HungBuilder = (g, x, y, def) => {
  // A coat is a coat's size (2.6 ft from the hook) whatever the catalogue's box says; a bigger box makes a longer one.
  const drop = Math.max(2.6, def.drop ?? def.size.h * 0.85);
  hangingCoat(g, x, y + 0.3, 0.35, drop - 0.3, 0x4a5a7a);
  hangingCoat(g, x + 0.1, y + 0.28, 0.2, drop - 0.6, 0xb8a98c);
};
const HUNG_BUILDERS: Record<string, HungBuilder> = {
  'rake': rake, 'shovel': shovel, 'broom': broom, 'hose': hose, 'wall-bin': wallBin, 'shelf-tools': shelfTools,
  'shelf-bottles': shelfBottles, 'shelf-books': shelfBooks, 'basket': basketProp, 'ironing-board': ironingBoard, 'towel-bar': towelBar,
  'bike-hung': bikeHung, 'helmet': helmet, 'hockey-sticks': hockeySticks, 'skateboard': skateboard, 'axe': axe, 'backpack': backpack,
  'bike-pump': bikePump, 'cord': cord, 'hand-tools': handTools, 'boots-basket': bootsBasket, 'balls-basket': ballsBasket,
  'shelf-helmet': shelfHelmet, 'shelf-drill': shelfDrill, 'coats-hanging': coatsHanging,
};
/** Every prop id that has a builder (kits are checked against this). */
export const DRAWABLE_PROP_IDS = new Set([...Object.keys(FLOOR_BUILDERS), ...Object.keys(HUNG_BUILDERS)]);

/**
 * A floor prop, standing at the origin facing +z. The viewer positions and
 * turns the group. Unknown ids get a plain grey box of the prop's size.
 *
 * Always the procedural stand-in: no prop swaps in a model file here (the car
 * and truck once did — 6.5 MB / 94k triangles and 1.3 MB / 19k, loaded with
 * the previews off). Photoreal models are the "Model previews" toggle's.
 */
export function buildFloorProp(def: PropDef): THREE.Group {
  const g = new THREE.Group();
  g.name = `prop-${def.id}`;
  const builder = FLOOR_BUILDERS[def.id];
  if (builder) builder(g, def.size);
  else box(g, def.size.w, def.size.h, def.size.d, 0, def.size.h / 2, 0, C.grey);
  return g;
}

/**
 * A prop hung from the ceiling, its origin the point it is held at: a plain
 * box of its size hanging below, on two short straps. (Its photoreal model,
 * from Prop Studio, hangs from the same point in the render.)
 */
export function buildCeilingProp(def: PropDef): THREE.Group {
  const g = new THREE.Group();
  g.name = `prop-${def.id}`;
  const { w, d, h } = def.size;
  const strap = Math.min(0.25, h * 0.2);
  box(g, w, h - strap, d, 0, -strap - (h - strap) / 2, 0, C.grey);
  for (const sx of [-w * 0.35, w * 0.35]) box(g, 0.08, strap, 0.04, sx, -strap / 2, 0, C.black);
  return g;
}

/**
 * A hung or wall prop in canonical wall coordinates, held at (x, y) — a
 * hook's prong, a shelf's top, a basket's floor — and drawn from there. The
 * builders draw about 0.3' out from the wall; `holdZ` says how far out the
 * accessory really holds it, and the parts are moved out to suit. The viewer
 * applies the wall's face transform.
 */
export function buildHungProp(def: PropDef, x: number, y: number, holdZ?: number): THREE.Group {
  const g = new THREE.Group();
  g.name = `prop-${def.id}`;
  const builder = HUNG_BUILDERS[def.id];
  if (builder) builder(g, x, y, def);
  // No builder of its own (a prop made in Prop Studio): its box, centred on
  // the hold point — 0.3' out like the builders' holds, so the move below
  // puts its centre at `holdZ`, as the frame's box is (propFrame.ts).
  else box(g, def.size.w, def.size.h, def.size.d, x, y - (def.drop ?? 0) + def.size.h / 2, 0.3, C.grey);
  // The hold point as set for the prop: out from the wall (the accessory's
  // reach, or the prop's own), and along the wall from the box's centre —
  // the prop moves the other way so the hold stays at (x, y).
  const z = holdZ ?? def.holdZ;
  const dz = z !== undefined ? z - 0.3 : 0;
  const dx = -(def.holdX ?? 0);
  if (Math.abs(dz) > 1e-6 || Math.abs(dx) > 1e-6) g.traverse((c) => { if ((c as THREE.Mesh).isMesh) { c.position.z += dz; c.position.x += dx; } });
  return g;
}
